// utils/apartmentUtils.js
import { APARTMENT_MAP } from "@/IPA/Apartments";
import { isNumericFloor, normalizeFloorValue } from "@/utils/floorUtils";

/**
 * Returns a human label for apartment:
 * - number => map to enum name (e.g., 1 -> "Apartament 1")
 * - numeric string "1" => number -> same mapping
 * - other strings => returned as-is
 * - null/"" => "Fără apartament"
 */
export function getApartamentLabel(v) {
  if (v == null || v === "") return "Fără apartament";

  // normalize numeric strings like "1" -> 1
  if (typeof v === "string" && /^\d+$/.test(v.trim())) v = Number(v);

  if (typeof v === "number") return APARTMENT_MAP[v] ?? String(v);
  return String(v);
}

/**
 * Group key keeps the label, not the raw value,
 * so group headers show the human-readable apartment name.
 */
export function makeGroupKey(apartament, floor) {
  const label = getApartamentLabel(apartament);
  const f = normalizeFloorValue(floor) || "0";
  return `${label}|${f}`;
}
export function getUnitId(apartament) {
  if (apartament == null || apartament === "") return null;

  // dacă e numeric (inclusiv string "2")
  if (!isNaN(Number(apartament))) {
    return `unit_${Number(apartament)}`;
  }

// fallback pentru text manual
return `unit_${String(apartament).trim().replace(/\s+/g, "_")}`;
}
/**
 * Returns true if the given apartment value represents a "copy" group.
 * Copy apartments are encoded either as:
 * - negative numeric / numeric-string values (derived from original apartment)
 * - or "__copy__<original>" string markers for non-numeric apartments
 *
 * @param {string|number|null} value
 * @returns {boolean}
 */
export function isCopyApartment(value) {
  if (value == null) return false;

  if (typeof value === "number") {
    return value < 0;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("__copy__")) return true;
    if (/^-?\d+$/.test(trimmed) && Number(trimmed) < 0) return true;
  }

  return false;
}

/**
 * Finds apartments that are assigned to more than one floor.
 * Ignores:
 * - rooms with area <= 0
 * - copy floors (floor < 0)
 * - copy apartments (see isCopyApartment)
 * - empty / "0" apartments
 *
 * @param {Array<Object>} rooms - Raw rooms array (from location).
 * @returns {Array<{ aptKey: string, floors: string[] }>}
 */
export function findApartmentFloorConflicts(rooms) {
  const aptFloors = {};

  for (const room of rooms || []) {
    const area = Number(room?.area?.sq_m ?? 0);
    if (area <= 0) continue;

    const normalizedFloor = normalizeFloorValue(room?.floor);
    const aptRaw = room?.apartament;

    if (
      !normalizedFloor ||
      (isNumericFloor(normalizedFloor) && Number(normalizedFloor) < 0) ||
      isCopyApartment(aptRaw)
    ) {
      continue;
    }

    const aptKey = String(aptRaw ?? "").trim().toLowerCase();
    if (!aptKey || aptKey === "0") continue;

    if (!aptFloors[aptKey]) aptFloors[aptKey] = new Set();
    aptFloors[aptKey].add(normalizedFloor);
  }

  return Object.entries(aptFloors)
    .filter(([, floors]) => floors.size > 1)
    .map(([aptKey, floors]) => ({
      aptKey,
      floors: [...floors],
    }));
}